/* eslint-disable @typescript-eslint/no-deprecated */
import clsx from 'clsx';
import {
  type HTMLAttributes,
  type RefObject,
  useCallback,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import type { Entries } from 'type-fest';

import { DetailView } from './DetailView.js';
import { Header } from './Header.js';
import { Main } from './Main.js';
import {
  pageContext,
  type PageContext,
  type PageLayoutState,
} from './page-context.js';
import type { PagePanelSizes, PanelName } from './page-types.js';
import * as styles from './Page.sty.js';
import { GAP_SIZE } from './panel.sty.js';
import { PanelControlButton } from './PanelControlButton.js';
import { Sidebar } from './Sidebar.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';

const COMPONENT_NAME = 'Page';

/**
 * @public
 * @deprecated The `Page` component is deprecated and will be removed in a future release. Please use `PageLayout` instead.
 */
export interface PageProps
  extends WithChildren,
    Omit<HTMLAttributes<HTMLDivElement>, keyof StylingProps>,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * Whether animations should be disabled.
   * If true or 'prefers-reduced-motion' is set to 'reduce', no animations are applied.
   * @defaultValue false
   */
  disableAnimations?: boolean;
}

interface RegisterPanelPayload {
  panel: PanelName;
  ref: RefObject<HTMLDivElement | null>;
  isControlled: boolean;
  isDismissed: boolean;
  minWidth?: number;
  breakpoint?: number;
}

type PANELACTION =
  | { type: 'register'; payload: RegisterPanelPayload }
  | { type: 'remove'; payload: { panel: PanelName } }
  | { type: 'dismiss'; payload: { panel: PanelName } }
  | { type: 'expand'; payload: { panel: PanelName } }
  | { type: 'toggle'; payload: { panel: PanelName } };

function handleRegisterAction(
  state: PageLayoutState,
  payload: RegisterPanelPayload,
): PageLayoutState {
  const { panel, ref, isControlled, isDismissed, minWidth, breakpoint } =
    payload;

  if (state[panel]) {
    return state;
  }

  const newState = { ...state };
  if (panel === 'main') {
    newState[panel] = {
      ref,
      isControlled: false,
      isDismissed: false,
      minWidth: minWidth ?? 0,
      breakpoint: 0,
    };
    return newState;
  }
  newState[panel] = {
    ref,
    isControlled,
    isDismissed,
    minWidth: minWidth ?? 0,
    breakpoint: breakpoint ?? 0,
  };

  return newState;
}

function handleRemoveAction(
  state: PageLayoutState,
  payload: { panel: PanelName },
): PageLayoutState {
  const { panel } = payload;

  const newState = { ...state, [panel]: null };

  return newState;
}

function handleDismissAction(
  state: PageLayoutState,
  payload: { panel: PanelName },
): PageLayoutState {
  const { panel } = payload;

  if (panel === 'main') {
    return state;
  }

  const newState = { ...state };
  if (!newState[panel]) {
    return state;
  }

  const isControlled = newState[panel].isControlled;
  if (isControlled) {
    return state;
  }

  newState[panel] = {
    ...newState[panel],
    isDismissed: true,
  };

  return newState;
}

function handleExpandAction(
  state: PageLayoutState,
  payload: { panel: PanelName },
): PageLayoutState {
  const { panel } = payload;

  if (panel === 'main') {
    return state;
  }

  const newState = { ...state };
  if (!newState[panel]) {
    return state;
  }

  const isControlled = newState[panel].isControlled;
  if (isControlled) {
    return state;
  }

  newState[panel] = {
    ...newState[panel],
    isDismissed: false,
  };

  return newState;
}

function handleToggleAction(
  state: PageLayoutState,
  payload: { panel: PanelName },
): PageLayoutState {
  const { panel } = payload;

  if (panel === 'main') {
    return state;
  }

  const newState = { ...state };
  if (!newState[panel]) {
    return state;
  }

  const isControlled = newState[panel].isControlled;
  if (isControlled) {
    return state;
  }

  newState[panel] = {
    ...newState[panel],
    isDismissed: !newState[panel].isDismissed,
  };

  return newState;
}

function pageLayoutReducer(
  state: PageLayoutState,
  action: PANELACTION,
): PageLayoutState {
  if (action.type === 'register') {
    return handleRegisterAction(state, action.payload);
  }

  if (action.type === 'remove') {
    return handleRemoveAction(state, action.payload);
  }

  if (action.type === 'dismiss') {
    return handleDismissAction(state, action.payload);
  }

  if (action.type === 'expand') {
    return handleExpandAction(state, action.payload);
  }

  if (action.type === 'toggle') {
    return handleToggleAction(state, action.payload);
  }

  throw Error('invalid action.type');
}

function getGridColumnStyle(
  panel: {
    isDismissed: boolean;
    minWidth?: number;
    breakpoint?: number;
  },
  max: string = 'auto',
) {
  const hasDrawer = !window.matchMedia(`(min-width: ${panel.breakpoint}px)`)
    .matches;

  if (hasDrawer || panel.minWidth === undefined || panel.isDismissed) {
    return `minmax(min-content, ${max})`;
  }

  return `minmax(${panel.minWidth + GAP_SIZE}px, ${max})`;
}

function getGridTemplateStyles(panels: PageLayoutState) {
  let columns = '';
  let contentAreas = '';
  let headerAreas = '';

  if (panels.main) {
    columns = `${getGridColumnStyle(panels.main, '1fr')} ${columns}`;
    contentAreas = 'main';
    headerAreas = 'header';
  }

  if (panels.sidebar) {
    columns = `${getGridColumnStyle(panels.sidebar)} ${columns}`;
    contentAreas = `sidebar ${contentAreas}`;
    headerAreas = `header ${headerAreas}`;
  }

  if (panels.details) {
    columns = `${columns} ${getGridColumnStyle(panels.details)}`;
    contentAreas = `${contentAreas} details`;
    headerAreas = `${headerAreas} header`;
  }

  return {
    gridTemplateColumns: columns,
    gridTemplateAreas: `"${headerAreas}" "${contentAreas}"`,
    gridTemplateRows: 'min-content minmax(0, 1fr)',
  };
}

const PageRoot = (props: PageProps) => {
  const { children, style, className, ...remainingProps } = props;
  const [slots, otherChildren] = useSlots(children, {
    header: Header,
  });

  const [panels, dispatch] = useReducer(pageLayoutReducer, {
    sidebar: null,
    main: null,
    details: null,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useContainerSize(containerRef);

  const getPanelSizes = useCallback(() => {
    const sizes: PagePanelSizes = {};

    for (const [key, panel] of Object.entries(panels) as Entries<
      typeof panels
    >) {
      if (!panel?.ref.current) {
        continue;
      }
      const panelSize = parseFloat(getComputedStyle(panel.ref.current).width);
      sizes[key] = {
        px: panelSize,
        '%': (panelSize / containerSize.width) * 100,
      };
    }

    return sizes;
  }, [containerSize.width, panels]);

  const registerPanel = useCallback<PageContext['registerPanel']>(
    (panelName, { ref, isControlled, isDismissed, minWidth, breakpoint }) => {
      dispatch({
        type: 'register',
        payload: {
          panel: panelName,
          ref,
          isControlled,
          isDismissed,
          minWidth,
          breakpoint,
        },
      });
    },
    [],
  );

  const removePanel = useCallback<PageContext['removePanel']>((panelName) => {
    dispatch({ type: 'remove', payload: { panel: panelName } });
  }, []);

  const dismissPanel = useCallback<PageContext['dismissPanel']>((panelName) => {
    dispatch({
      type: 'dismiss',
      payload: { panel: panelName },
    });
  }, []);

  const expandPanel = useCallback<PageContext['expandPanel']>((panelName) => {
    dispatch({
      type: 'expand',
      payload: { panel: panelName },
    });
  }, []);

  const togglePanel = useCallback<PageContext['togglePanel']>((panelName) => {
    dispatch({
      type: 'toggle',
      payload: { panel: panelName },
    });
  }, []);

  const gridTemplateStyles = getGridTemplateStyles(panels);

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const ctx = useMemo(
    () => ({
      containerSize: containerSize.width,
      panels,
      registerPanel,
      removePanel,
      dismissPanel,
      expandPanel,
      togglePanel,
      getPanelSizes,
    }),
    [
      containerSize.width,
      panels,
      registerPanel,
      removePanel,
      dismissPanel,
      expandPanel,
      togglePanel,
      getPanelSizes,
    ],
  );

  return (
    <div
      {...restProps}
      {...behavioralTrackingProps}
      ref={containerRef}
      className={clsx(styles.page, className)}
      style={{ ...style, ...gridTemplateStyles }}
      data-dt-component={COMPONENT_NAME}
    >
      {slots.header}
      <pageContext.Provider value={ctx}>{otherChildren}</pageContext.Provider>
    </div>
  );
};

/**
 * The `Page` component provides the basic layout for your app with slots for a header, sidebar, main content, and a detail view.
 * The `Main` slot is the only slot that is mandatory for rendering the `Page`; every other slot is optional.
 * @public
 * @deprecated The `Page` component is deprecated and will be removed in a future release. Please use `PageLayout` instead.
 */
export const Page = Object.assign(PageRoot, {
  /** The `Header` component is used inside the `Page` component for displaying content at the very top of the page. */
  Header,
  /** The `Sidebar` component is used inside the `Page` component for displaying content in the corresponding panel. */
  Sidebar,
  /**
   * The `Main` component is used inside the `Page` component for displaying content in the corresponding panel.
   * It is the only compound that is mandatory for rendering the `Page`.
   */
  Main,
  /** The `DetailView` component is used inside the `Page` component for displaying content in the corresponding panel. */
  DetailView,
  /**
   * Use the `PanelControlButton` to get the correct icon for toggling the sidebar / detail view.
   * Depending on where it is rendered and the current state of the panel, the corresponding icon is rendered.
   * You can customize the rendered content by passing any children.
   */
  PanelControlButton,
});
